// Scan journey tracking → Kleep events collector → ClickHouse events_raw
// (event_type "drawer_flow", app "webapp"). It measures where shoppers drop,
// wait or retry while scanning their foot. The drawer mints the journey id and
// passes it as `flow_id` (and the scan attempt as `attempt`) in this page's
// URL, so the phone steps join the drawer's. Opened without it (an old QR
// code, a shared link), the page starts its own journey.
//
// Fire-and-forget: nothing here may throw or slow the scan down. Not sent from
// localhost or with ?disableTracking=true.

const COLLECTOR_URL =
  process.env.REACT_APP_EVENTS_COLLECTOR_URL ||
  "https://events.kleep.ai/analytics/push";

type FlowEnv = "production" | "staging" | "development" | "local";

export interface FlowEventProps {
  step?: string | null;
  stepMs?: number;
  reason?: string | number | null;
  value?: string | number | boolean | null;
}

const params = new URLSearchParams(window.location.search);

const newId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const flowIdFromUrl = params.get("flow_id");
const flowId = flowIdFromUrl || newId();
const flowStart = Date.now();
const attemptFromUrl = parseInt(params.get("attempt") || "", 10);
// Scans started on this page (Restart and Retry start a new one).
let pageAttempt = 0;

/** The journey was started by the drawer (vs this page opened on its own). */
export const isLinkedFlow = (): boolean => !!flowIdFromUrl;

const flowEnv = (): FlowEnv => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "local";
  if (host.includes("dev")) return "development";
  if (host.includes("staging")) return "staging";
  return "production";
};

const platform = (): "ios" | "android" | "desktop" | "other" => {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  // iPadOS reports itself as a Mac with touch.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Windows|Macintosh|Linux|CrOS/i.test(ua)) return "desktop";
  return "other";
};

// In-app browsers often block the camera or the motion sensors.
const inAppBrowser = (): string | undefined => {
  const ua = navigator.userAgent || "";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
  if (/musical_ly|TikTok|BytedanceWebview/i.test(ua)) return "tiktok";
  if (/Snapchat/i.test(ua)) return "snapchat";
  if (/LinkedInApp/i.test(ua)) return "linkedin";
  if (/Pinterest/i.test(ua)) return "pinterest";
  if (/Line\//i.test(ua)) return "line";
  if (/; wv\)/i.test(ua)) return "android_webview";
  return undefined;
};

const isEnabled = (): boolean =>
  flowEnv() !== "local" && params.get("disableTracking") !== "true";

/** A scan starts (first one, Restart or Retry). Returns its number in the journey. */
export const countScanAttempt = (): number => {
  pageAttempt += 1;
  return currentAttempt();
};

const currentAttempt = (): number =>
  (Number.isFinite(attemptFromUrl) ? Math.max(attemptFromUrl, 1) : 1) +
  Math.max(pageAttempt - 1, 0);

const send = (body: Record<string, unknown>) => {
  try {
    const blob = new Blob([JSON.stringify(body)], {
      type: "text/plain;charset=UTF-8",
    });
    // text/plain keeps it a CORS "simple" request (no preflight); sendBeacon
    // survives the page being closed or sent to the background.
    if (navigator.sendBeacon?.(COLLECTOR_URL, blob)) return;
    void fetch(COLLECTOR_URL, {
      method: "POST",
      body: blob,
      keepalive: true,
      mode: "cors",
    }).catch(() => {});
  } catch {
    // Tracking must never surface an error.
  }
};

/**
 * One thing that happened in the scan journey. `name` says what
 * (step_viewed, camera_error, level_reached, trigger_failed…), `step` where.
 */
export const trackScanFlow = (
  name: string,
  props: FlowEventProps = {}
): void => {
  if (!isEnabled()) return;

  const retailer = params.get("domain");
  if (!retailer) return;

  const toText = (v: unknown) =>
    v === null || v === undefined ? undefined : String(v);

  send({
    event: "drawer_flow",
    retailer,
    user_id: params.get("uid"),
    timestamp: Date.now(),
    data: {
      flow: "footwear",
      app: "webapp",
      flowId,
      name,
      step: props.step ?? undefined,
      stepMs: props.stepMs,
      elapsedMs: Math.max(0, Date.now() - flowStart),
      attempt: currentAttempt(),
      reason: toText(props.reason),
      value: toText(props.value),
      platform: platform(),
      mobile: window.innerWidth <= 767,
      inApp: inAppBrowser(),
      productId: params.get("product_id") ?? undefined,
      locale: params.get("lang") || undefined,
      env: flowEnv(),
    },
  });
};
